import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Mail, Lock, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAccount = () => {
  const { user, logout, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    currentPassword: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    
    if (!emailForm.newEmail || !emailForm.currentPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(emailForm.newEmail)) {
      toast.error('Please enter a valid email');
      return;
    }

    setEmailLoading(true);

    try {
      const response = await axios.put(`${API}/auth/update-email`, {
        newEmail: emailForm.newEmail,
        currentPassword: emailForm.currentPassword
      }, {
        headers: getAuthHeader()
      });

      // Update token in localStorage
      if (response.data.token) {
        localStorage.setItem('goat_admin_token', response.data.token);
      }

      toast.success('Email updated successfully! Please log in again.');
      setEmailForm({ newEmail: '', currentPassword: '' });
      
      // Force re-login with new credentials
      setTimeout(() => {
        logout();
        navigate('/admin');
      }, 1500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      await axios.put(`${API}/auth/update-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, {
        headers: getAuthHeader()
      });

      toast.success('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#09090b] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-heading font-bold text-lg tracking-tight">
              G.O.A.T. Admin
            </span>
            <nav className="hidden md:flex gap-4">
              <Link
                to="/admin/dashboard"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Products
              </Link>
              <Link
                to="/admin/orders"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Orders
              </Link>
              <Link
                to="/admin/subscribers"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Subscribers
              </Link>
              <Link
                to="/admin/account"
                className="text-white font-medium text-sm uppercase tracking-wider"
              >
                My Account
              </Link>
            </nav>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl uppercase tracking-tight flex items-center gap-3">
            <User className="w-6 h-6" />
            My Account
          </h1>
          <p className="text-zinc-500 mt-1">Manage your admin credentials</p>
        </div>

        {/* Current Email Display */}
        <div className="bg-zinc-50 p-6 mb-8 border border-zinc-200">
          <p className="text-sm text-zinc-500 mb-1">Current Email</p>
          <p className="font-bold text-lg" data-testid="current-email">{user?.email}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Update Email */}
          <div className="border border-zinc-200 p-6">
            <div className="flex items-center gap-2 mb-6">
              <Mail className="w-5 h-5 text-zinc-600" />
              <h2 className="font-heading font-bold text-lg uppercase tracking-tight">
                Change Email
              </h2>
            </div>
            
            <form onSubmit={handleUpdateEmail} className="space-y-4">
              <div>
                <Label htmlFor="newEmail">New Email</Label>
                <Input
                  id="newEmail"
                  type="email"
                  value={emailForm.newEmail}
                  onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                  className="mt-1"
                  data-testid="new-email-input"
                />
              </div>
              <div>
                <Label htmlFor="emailCurrentPassword">Current Password</Label>
                <Input
                  id="emailCurrentPassword"
                  type="password"
                  value={emailForm.currentPassword}
                  onChange={(e) => setEmailForm({ ...emailForm, currentPassword: e.target.value })}
                  className="mt-1"
                  data-testid="email-current-password"
                />
              </div>
              <Button
                type="submit"
                disabled={emailLoading}
                className="w-full bg-[#09090b] text-white hover:bg-zinc-800"
                data-testid="update-email-btn"
              >
                {emailLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Update Email'
                )}
              </Button>
            </form>
          </div>

          {/* Update Password */}
          <div className="border border-zinc-200 p-6">
            <div className="flex items-center gap-2 mb-6">
              <Lock className="w-5 h-5 text-zinc-600" />
              <h2 className="font-heading font-bold text-lg uppercase tracking-tight">
                Change Password
              </h2>
            </div>
            
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="mt-1"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="mt-1"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={passwordLoading}
                className="w-full bg-[#09090b] text-white hover:bg-zinc-800"
                data-testid="update-password-btn"
              >
                {passwordLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Update Password'
                )}
              </Button>
            </form>
          </div>
        </div>

        <p className="text-zinc-400 text-sm mt-8 text-center">
          After changing your email, you'll need to log in again with the new credentials.
        </p>
      </main>
    </div>
  );
};
